import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, Users, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { ROLES, ROLE_ORDER, roleBadgeClass } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { name: "", email: "", password: "", role: "technician" };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || (!editing && (!form.email || !form.password))) {
      toast.error("Lengkapi semua field wajib");
      return;
    }
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Perubahan disimpan");
      } else {
        await api.post("/users", form);
        toast.success("User ditambahkan");
      }
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal menyimpan";
      toast.error(typeof d === "string" ? d : "Gagal menyimpan");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus user ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Dihapus");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal menghapus";
      toast.error(typeof d === "string" ? d : "Gagal menghapus");
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Manajemen Pengguna
          </h1>
          <p className="text-sm text-slate-500 mt-1">Atur akun & level akses staff.</p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-slate-900 hover:bg-slate-800 gap-2"
          data-testid="add-user-button"
        >
          <UserPlus className="w-4 h-4" /> Tambah Pengguna
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          <div className="col-span-full flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="col-span-full bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
            <Users className="w-8 h-8 mx-auto text-slate-400 mb-3" />
            <p className="text-slate-500">Belum ada pengguna.</p>
          </div>
        ) : (
          users.map((u) => (
            <div
              key={u.id}
              data-testid={`user-card-${u.id}`}
              className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="w-11 h-11 rounded-full bg-slate-900 text-white font-bold flex items-center justify-center text-lg">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${roleBadgeClass(u.role)}`}
                >
                  {ROLES[u.role]?.label || u.role}
                </span>
              </div>
              <div className="font-medium text-slate-900 mt-2">{u.name}</div>
              <div className="text-xs text-slate-500 truncate">{u.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                {u.auth_provider === "google" ? "Google" : "Password"}
              </div>
              <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1 h-8"
                  onClick={() => openEdit(u)}
                  data-testid={`edit-user-${u.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Button>
                {u.id !== me?.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 h-8"
                    onClick={() => remove(u)}
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nama Lengkap</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="user-form-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="user-form-email"
              />
              {editing && <div className="text-[10px] text-slate-400 mt-1">Email tidak dapat diubah</div>}
            </div>
            <div>
              <Label>Password {editing && <span className="text-slate-400 font-normal">(kosongkan bila tidak diubah)</span>}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={editing ? "Password baru (opsional)" : "min. 6 karakter"}
                data-testid="user-form-password"
              />
            </div>
            <div>
              <Label>Level / Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_ORDER.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLES[r].label} — <span className="text-slate-500 text-xs">{ROLES[r].desc}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-slate-900" data-testid="user-form-submit">
              {editing ? "Simpan" : "Tambah"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
